<script setup>
/**
 * One policy (XN-4556, XN-4670): what the agency actually holds and the
 * policyholder can act on — the number in full, the renewal date and the
 * monthly premium. Nothing about what the policy covers: the portal is not the
 * policy document, and a summary that disagrees with the wording is worse than
 * none. A lapsed or cancelled policy still opens, and says so. Prototype
 * `policy()`, build 30 Sep 12:40.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import IconBack from '../icons/IconBack.vue';
import IconDownload from '../icons/IconDownload.vue';
import IconMail from '../icons/IconMail.vue';
import CoverageArt from './CoverageArt.vue';
import CoverageRing from './CoverageRing.vue';
import { emailPolicy, myPolicy } from '../../api/portal';
import { reportError } from '../../faro';
import { useBackTo } from '../../composables/useBackTo';
import { useEventStore } from '../../stores/event';
import { viewerTimeZone } from '../../helpers/greeting';
import { askDraft, longDate, money, policyTitle, renewal, ringFor, rowLine, whoToAsk } from '../../helpers/coverage';

const NO_DATE = "No renewal date on file, so renewal reminders aren't running for this policy.";

const route = useRoute();
const router = useRouter();
const eventStore = useEventStore();

const id = computed(() => Number(route.params.id));
const state = ref('loading'); // loading | ready | missing | refused | error
const answer = ref(null);
const now = ref(new Date());
const titleEl = ref(null);

const policy = computed(() => answer.value?.policy ?? null);
const agency = computed(() => answer.value?.agency?.name || eventStore.event?.value?.login_event_name || 'your agency');
const ask = computed(() => whoToAsk(answer.value?.broker ?? null, agency.value));
// renewal() and ringFor() know nothing of status: a lapsed policy has no countdown at all.
const active = computed(() => policy.value?.status === 'active');
const when = computed(() => (policy.value ? renewal(policy.value, now.value) : null));
const dial = computed(() => (active.value && when.value ? ringFor(when.value) : null));
const heading = computed(() => [policy.value?.policy_number, policy.value?.description].filter(Boolean).join(' · '));
const held = computed(() => (policy.value?.since ? `with us since ${longDate(policy.value.since)}` : ''));
const heroLine = computed(() => [active.value ? when.value?.detail : '', held.value].filter(Boolean).join(' · '));
const renews = computed(() => {
  if (!when.value) return '';
  if (when.value.state === 'missing') return active.value ? NO_DATE : 'No renewal date on file.';
  if (!active.value) return when.value.date;
  const { top, bottom } = rowLine(when.value);
  return `${top} · ${bottom}`;
});
const premium = computed(() => money(policy.value?.monthly_premium, policy.value?.currency));

// Only the latest read may touch the page: a slow answer for the policy just
// left, or an earlier read of this same one (7 → 8 → 7), arrives too late.
let reads = 0;

async function load() {
  const read = ++reads;
  state.value = 'loading';
  try {
    const found = await myPolicy(id.value);
    if (read !== reads) return;
    answer.value = found;
    now.value = new Date();
    state.value = 'ready';
  } catch (error) {
    if (read !== reads) return;
    const status = error?.response?.status;
    if (status === 404) state.value = 'missing';
    // Staff previewing the portal are refused /my/* by design.
    else if (status === 403) state.value = 'refused';
    else {
      reportError(error);
      state.value = 'error';
    }
  }
}
watch(id, load, { immediate: true });

// Arriving, or reaching "not found", starts the reader at the title, not at <body>.
watch(state, async (value) => {
  if (value !== 'ready' && value !== 'missing') return;
  await nextTick();
  titleEl.value?.focus();
});

// Back to wherever they came from: the list, My details' coverage tags, an email.
const { label: backLabel, go: goBack } = useBackTo({ to: '/hub/coverage', label: 'All coverage', list: /^\/hub\/coverage(\?|$)/ });

// Written for them in Messages, editable, and never sent on its own.
function askAbout() {
  router.push({ path: '/hub/messages', state: { draft: askDraft(policy.value) } });
}

// Email me these details (XN-4611). The button holds its focus throughout —
// guarded by aria-disabled and the handler, never the native attribute while
// it may be focused — and one live region, there from the start, says each
// state out loud.
const RESET_MS = 30000;
const emailState = ref('idle'); // idle | sending | sent | already | limit | failed
const sentTo = ref('');
let resetTimer = null;
const clearReset = () => { clearTimeout(resetTimer); resetTimer = null; };
const settle = (next) => {
  clearReset();
  emailState.value = next;
  if (next === 'sent' || next === 'already') resetTimer = setTimeout(() => { emailState.value = 'idle'; }, RESET_MS);
};
const hasAddress = computed(() => Boolean(answer.value?.email_to));
const emailLabel = computed(() => ({
  sending: 'Sending…',
  sent: `Sent to ${sentTo.value}`,
  already: 'Already on its way — check your inbox',
})[emailState.value] ?? 'Email me these details');
const emailNote = computed(() => {
  if (!active.value) return '';
  if (!hasAddress.value) return "There's no email address on your account.";
  return ({
    sending: 'Sending…',
    sent: `Sent to ${sentTo.value}`,
    already: 'Already on its way — check your inbox',
    limit: "You've emailed yourself 10 times today.",
    failed: "Couldn't send — try again.",
  })[emailState.value] ?? '';
});
// Shown on screen when it says something the button does not; otherwise it only speaks.
const noteShown = computed(() => active.value && (!hasAddress.value || emailState.value === 'limit' || emailState.value === 'failed'));

function settleSent(result) {
  if (result?.already) {
    settle('already');
    return;
  }
  sentTo.value = result?.to ?? '';
  settle('sent');
}

function settleFailed(error) {
  const status = error?.response?.status;
  if (status === 429) settle('limit');
  else if (status === 422 || status === 404) {
    // The policy changed under the page (lapsed, or gone): show it as it is now.
    settle('idle');
    load();
  } else settle('failed');
}

async function emailMe() {
  if (emailState.value === 'sending' || !hasAddress.value) return;
  const asked = id.value;
  clearReset();
  emailState.value = 'sending';
  try {
    const result = await emailPolicy(asked, { timezone: viewerTimeZone(), locale: Intl.NumberFormat().resolvedOptions().locale });
    if (asked === id.value) settleSent(result);
  } catch (error) {
    if (asked === id.value) settleFailed(error);
  }
}

watch(id, () => { clearReset(); emailState.value = 'idle'; });
onBeforeUnmount(clearReset);
</script>

<template>
  <section class="pol">
    <XlButton variant="ghost" size="sm" class="pol__back" data-testid="policy-back" @click="goBack">
      <template #icon><IconBack color="currentColor" width="15" height="15" /></template>
      {{ backLabel }}
    </XlButton>

    <div v-if="state === 'loading'" aria-busy="true" data-testid="policy-loading"><XlSkeleton height="300px" /></div>

    <div v-else-if="state === 'error'" class="pol__error" role="alert">
      <p>This policy can't be shown right now.</p>
      <XlButton variant="ghost" size="sm" data-testid="policy-retry" @click="load()">Retry</XlButton>
    </div>

    <div v-else-if="state === 'missing'" class="pol__card pol__pad" data-testid="policy-missing">
      <h1 ref="titleEl" class="pol__title" tabindex="-1">Policy not found</h1>
      <RouterLink to="/hub/coverage" class="pol__back-link"><IconBack /> Back to My coverage</RouterLink>
    </div>

    <p v-else-if="state === 'refused'" class="pol__calm" data-testid="policy-refused">My coverage shows a policyholder their own policies.</p>

    <template v-else-if="policy">
      <div class="pol__hero">
        <CoverageArt :line="policy.line" :height="150" />
        <div class="pol__hero-bd">
          <h1 ref="titleEl" class="pol__title" tabindex="-1">{{ policyTitle(policy) }}</h1>
          <p class="pol__num">{{ heading }}</p>
          <div class="pol__foot">
            <CoverageRing v-if="dial" :percent="dial.percent" :label="dial.label" :soon="when.soon" />
            <p class="pol__fl">
              <b v-if="active" :class="{ 'pol__late': when.state === 'overdue' }">{{ when.headline }}</b>
              <span v-if="heroLine">{{ heroLine }}</span>
            </p>
          </div>
        </div>
      </div>

      <p v-if="!active" class="pol__notice" data-testid="policy-status">This policy is {{ policy.status }}.</p>

      <div class="pol__sh"><h2>The details</h2><i class="pol__ln" /></div>
      <dl class="pol__card pol__details">
        <div class="pol__row"><dt>Policy number</dt><dd>{{ policy.policy_number || 'Not on file' }}</dd></div>
        <div class="pol__row"><dt>{{ active ? 'Renews' : 'Renewal date' }}</dt><dd>{{ renews }}</dd></div>
        <div v-if="premium" class="pol__row"><dt>Monthly premium</dt><dd>{{ premium }}</dd></div>
      </dl>

      <div class="pol__actions" data-testid="policy-actions">
        <XlButton v-if="ask.has" data-testid="policy-ask" @click="askAbout">
          <template #icon><IconMail color="currentColor" size="16" /></template>
          Ask about this policy
        </XlButton>
        <p v-else class="pol__nobroker">{{ ask.noBroker }}</p>
        <XlButton
          v-if="active"
          variant="ghost"
          class="pol__email"
          data-testid="policy-email"
          :disabled="!hasAddress"
          :aria-disabled="emailState === 'sending' ? 'true' : undefined"
          @click="emailMe"
        >
          <template #icon><IconDownload color="currentColor" size="16" /></template>
          {{ emailLabel }}
        </XlButton>
      </div>
    </template>

    <p class="pol__email-note" :class="{ 'pol__sr': !noteShown }" aria-live="polite" data-testid="policy-email-note">
      {{ emailNote }}
      <RouterLink v-if="active && !hasAddress" to="/hub/details">My details</RouterLink>
      <button v-if="emailState === 'limit' && ask.has" type="button" class="pol__link" data-testid="policy-email-messages" @click="askAbout">Ask in Messages instead</button>
    </p>
  </section>
</template>

<style scoped>
.pol__back { margin-bottom: 18px; }
.pol__back-link { display: inline-flex; align-items: center; gap: 8px; color: var(--xl-color-primary-text); text-decoration: none; }
.pol__back-link:visited, .pol__back-link:hover, .pol__back-link:focus { color: var(--xl-color-primary-text); }
.pol__back-link:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.pol__hero {
  border-radius: var(--xl-radius-lg); background: #fff; border: 1px solid var(--xl-color-border);
  overflow: hidden; box-shadow: var(--xl-shadow-1); margin-bottom: 22px;
}
.pol__hero-bd { padding: 20px 22px 22px; }
.pol__title { font-size: 26px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 4px; overflow-wrap: anywhere; }
.pol__title:focus { outline: none; }
.pol__num { margin: 0; font-size: 14px; color: var(--xl-color-on-surface-variant); overflow-wrap: anywhere; }
.pol__foot { display: flex; align-items: center; gap: 10px; margin-top: 16px; }
.pol__fl { margin: 0; flex: 1; font-size: 13px; line-height: 1.35; }
.pol__fl b { display: block; font-size: 13.5px; }
.pol__fl span { color: var(--xl-color-outline); }
.pol__late { color: var(--xl-color-warning); }
.pol__notice {
  margin: 0 0 6px; padding: 11px 14px; border-radius: 12px; font-size: 14px;
  background: var(--xl-color-warning-container); color: var(--xl-color-warning);
}
.pol__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.pol__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.pol__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.pol__card { background: #fff; border: 1px solid var(--xl-color-border); border-radius: var(--xl-radius-lg); box-shadow: var(--xl-shadow-1); margin: 0 0 14px; }
.pol__pad { padding: 22px 24px; }
.pol__pad p { margin: 0 0 8px; }
.pol__details { padding: 8px 22px; }
.pol__row { display: flex; align-items: baseline; gap: 14px; padding: 15px 0; border-bottom: 1px solid var(--xl-color-border-subtle); }
.pol__row:last-child { border-bottom: 0; }
.pol__row dt { width: 200px; flex: 0 0 200px; font-size: 14px; color: var(--xl-color-outline); }
.pol__row dd { margin: 0; flex: 1; min-width: 0; font-weight: 500; overflow-wrap: anywhere; }
.pol__actions { display: flex; gap: 9px; flex-wrap: wrap; align-items: center; margin-top: 20px; }
.pol__actions :deep(.xl-btn[aria-disabled="true"]) { opacity: 0.7; cursor: progress; transform: none; box-shadow: none; }
/* "Sent to s•••@a-long-domain.example" can outgrow a 320px screen, and a button
   is one fixed-height line: this one label wraps instead. */
.pol__email { height: auto; min-height: 42px; max-width: 100%; padding-block: 9px; white-space: normal; overflow-wrap: anywhere; text-align: left; }
.pol__nobroker { margin: 0; color: var(--xl-color-on-surface-variant); }
.pol__email-note { margin: 10px 0 0; font-size: 13.5px; color: var(--xl-color-on-surface-variant); }
.pol__email-note a, .pol__link { color: var(--xl-color-primary); background: none; border: 0; padding: 0; cursor: pointer; font: inherit; }
.pol__sr { position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.pol__calm { margin: 0; color: var(--xl-color-on-surface-variant); }
.pol__error {
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 18px 20px;
  border-radius: 16px; font-size: 14.5px; background: var(--xl-color-error-container, #FBEAEA); border: 1px solid var(--xl-color-error); color: var(--xl-color-error);
}
.pol__error p { margin: 0; }
@media (max-width: 480px) {
  .pol__row { flex-direction: column; gap: 2px; }
  .pol__row dt { width: auto; flex: none; }
  .pol__details { padding: 4px 14px; }
}
</style>
